import { getIdToken } from "./user-utils";
// import { apiConfig } from '../../env-exports';

export async function checkResponseAndThrow(
  response: Response,
  errorMessage: string,
) {
  // console.log('apiConfig : ', apiConfig);
  if (response.status === 401) {
    throw new Error("Authentication failed. Please log in again.");
  }

  if (response.status === 403) {
    throw new Error("You do not have permission to access this resource.");
  }

  if (!response.ok) {
    // This is to extract error message from response body instead of always showing the generic error message. The server is expected to return an error message in the response body in case of an error.
    let serverMessage: string | null = null;
    try {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorBody = await response.clone().json();
        serverMessage =
          errorBody?.message || errorBody?.error || errorBody?.errorMessage;
      }
    } catch (parseError) {
      // if we can't parse the error body, continue to default error handling
    }

    // if we successfully extracted an error message from the server, use it
    if (serverMessage) {
      throw new Error(serverMessage);
    }

    // Provide more specific error messages based on status code
    if (response.status === 404) {
      throw new Error(
        "The requested resource was not found. Please check the URL and try again.",
      );
    } else if (response.status >= 500) {
      throw new Error(
        "A server error occurred. Please try again later or contact support if the problem persists.",
      );
    } else if (response.status === 400) {
      throw new Error(
        "Invalid request. Please check your input and try again.",
      );
    }

    throw new Error(errorMessage);
  }
}
type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export async function callApi(
  apiPath: string,
  options: {
    /**
     * @default "GET"
     */
    method?: Method;
    /**
     * @default "application/json"
     */
    contentType?: string;
    /**
     * @default undefined
     */
    body?: Record<string, unknown> | string;
    /**
     * @default true
     */
    checkErrors?: boolean;
    /**
     * @default undefined
     */
    accept?: string;
  } = {},
): Promise<Response> {
  const {
    method = "GET",
    contentType = "application/json",
    body,
    checkErrors = true,
    accept,
  } = options;

  const idToken = await getIdToken();
  const headers: HeadersInit = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": contentType,
    ...(accept && { Accept: accept }),
  };

  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URI}/${apiPath}`,
    {
      method,
      headers,
      body: body
        ? typeof body === "string"
          ? body
          : JSON.stringify(body)
        : undefined,
    },
  );

  if (checkErrors) {
    await checkResponseAndThrow(
      response,
      `Failed to ${method} the requested resource. Please try again.`,
    );
  }

  return response;
}
